<script setup lang="ts">
import { computed, ref, watch } from 'vue';

import { PhxsgButton } from '@sg/src/View/Components/Atoms/Button';
import { PhxsgCenter } from '@sg/src/View/Components/Atoms/Center';
import { PhxsgText } from '@sg/src/View/Components/Atoms/Text';

import { PhxsgSlider } from '../Slider';

import type { Swiper as SwiperType } from 'swiper/types';
import type { ComponentPublicInstance } from 'vue';

export interface SelectionSliderDetail {
  ref: string;
  name: string;
  cta: {
    label: string;
    href: string;
  };
}

export interface PhxsgSelectionSliderProps {
  label?: string;
  details?: SelectionSliderDetail[];
  prevButtonLabel?: string;
  nextButtonLabel?: string;
}

const {
  label,
  details = [],
  prevButtonLabel = 'Previous slide',
  nextButtonLabel = 'Next slide',
} = defineProps<PhxsgSelectionSliderProps>();

const rootRef = ref<ComponentPublicInstance | null>(null);
const swiperInstance = ref<SwiperType | null>(null);
const activeSlideIndex = ref(0);

const totalSlides = computed(() => swiperInstance.value?.slides?.length ?? details.length);
const currentSlideNumber = computed(() => activeSlideIndex.value + 1);

const slidePrev = () => {
  swiperInstance.value?.slidePrev();
};

const slideNext = () => {
  swiperInstance.value?.slideNext();
};

const onSwiper = (swiper: SwiperType) => {
  swiperInstance.value = swiper;

  swiper.on('slideChange', () => {
    activeSlideIndex.value = swiper.realIndex;
  });
};

const updateDetailVisibility = () => {
  const root = rootRef.value?.$el as Element | undefined;

  if (!root) {
    return;
  }

  const items = root.querySelectorAll<HTMLElement>('.phxsg-selection-slider__detail-item');

  items.forEach((item) => {
    const isTarget = Number(item.dataset.index) === activeSlideIndex.value;
    const wasActive = item.classList.contains('phxsg-selection-slider__detail-item--active');
    const wasLeaving = item.classList.contains('phxsg-selection-slider__detail-item--leaving');

    if (wasLeaving) {
      item.classList.remove('phxsg-selection-slider__detail-item--leaving');

      // Reflow to trigger the transition
      if (isTarget) {
        void item.offsetHeight;
      }
    }

    if (wasActive && !isTarget) {
      item.classList.add('phxsg-selection-slider__detail-item--leaving');
    }

    item.classList.toggle('phxsg-selection-slider__detail-item--active', isTarget);
  });
};

watch(activeSlideIndex, updateDetailVisibility);
</script>

<template>
  <PhxsgCenter
    ref="rootRef"
    :spacing="4"
    sizing="3xl"
    class="phxsg-selection-slider"
    role="group"
    aria-roledescription="carousel"
    :aria-label="label"
  >
    <div class="phxsg-selection-slider__start">
      <div class="phxsg-selection-slider__controls">
        <PhxsgButton
          size="sm"
          class="phxsg-selection-slider__nav-btn"
          :aria-label="prevButtonLabel"
          icon-only
          :icon="{ id: 'triangle-left', bidirectional: true }"
          @click="slidePrev"
        />

        <PhxsgButton
          size="sm"
          class="phxsg-selection-slider__nav-btn"
          :aria-label="nextButtonLabel"
          icon-only
          :icon="{ id: 'triangle-right', bidirectional: true }"
          @click="slideNext"
        />

        <PhxsgText class="phxsg-selection-slider__indicator">
          <span class="phxsg-selection-slider__indicator-current">{{ currentSlideNumber }}</span>
          <span>/{{ totalSlides }}</span>
        </PhxsgText>
      </div>
    </div>

    <PhxsgSlider
      class="phxsg-selection-slider__center"
      :navigation="false"
      :scrollbar="true"
      :effect="'fade'"
      :speed="750"
      :follow-finger="false"
      @swiper="onSwiper"
    >
      <slot name="pictures" />
    </PhxsgSlider>

    <slot />
  </PhxsgCenter>
</template>

<style src="./selection-slider.css" />
